import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, Form, notification, Spin } from "antd";

import {
  ImageInputField,
  TextAreaField,
  TextInputField,
  DropdownField,
  TagsDropdownField,
  SearchableSelectWithInput,
} from "../../../../../../../components/inputs";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../store/config";
import {
  createTrainingProgramStart,
  updateTrainingProgramStart,
} from "../../../../../../../store/features/training-program/training-program-slice";

import {
  AbsoluteButton55,
  RelativeFlex,
  StyledColumnWrapper,
  StyledForm,
  StyledLongLayout,
} from "../../../../../../../components/common/styles";
import {
  IMAGE_BASE_URL,
  VIDEO_BASE_URL,
} from "../../../../../../../common/config";
import { IUploadFileParams } from "../../../../../../../interfaces/files/files";
import { ICreateTrainingProgramPayload } from "../../../../../../../interfaces/store/training-program-interface";
import {
  useDifficultyOptions,
  useEquipmentTypeOptions,
  useFilterTags,
  useInterestsTags,
} from "../../../../../useSelectOptions";
import { blobToBase64 } from "../../../../../../../utils/utils";

export const TrainingProgramDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const { trainingProgramId } = useParams<{ trainingProgramId: string }>();
  const { id: trainerId } = useParams<{ id: string }>();
  const [api, contextHolder] = notification.useNotification();
  const { isEdit } = location.state || {};

  const isNew: boolean = location.pathname.includes("/training-programs/add");

  const [isEditable, setIsEditable] = useState(false);
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [largeImageData, setLargeImageData] =
    useState<IUploadFileParams | null>(null);
  const [videoData, setVideoData] = useState<IUploadFileParams | null>(null);

  const { trainingProgram, groups, loading, error, requestState, operation } =
    useAppSelector((state) => state.trainingProgram);

  const groupOptions = groups.map((group) => ({
    label: group,
    value: group,
  }));

  useEffect(() => {
    if (isNew || isEdit) {
      setIsEditable(true);
    }
  }, [isNew]);

  useEffect(() => {
    if (requestState === "finish") {
      if (operation === "update" || operation === "create") {
        setIsEditable(false);
      }
      if (operation === "create" && trainingProgram?._id) {
        navigate(
          `/trainers/${trainerId}/training-programs/${trainingProgram._id}`,
          {
            replace: true,
          }
        );
      }
    }
  }, [requestState, operation, trainingProgram, navigate, trainerId]);

  useEffect(() => {
    if (trainingProgram) {
      const workoutDurationValue = trainingProgram.workoutDuration
        ? trainingProgram.workoutDuration.toString()
        : trainingProgram.minWorkoutDuration &&
          trainingProgram.maxWorkoutDuration
        ? `${trainingProgram.minWorkoutDuration}-${trainingProgram.maxWorkoutDuration}`
        : "";

      form.setFieldsValue({
        name: trainingProgram.name,
        trainer: trainingProgram.trainer,
        description: trainingProgram.description,
        difficulty: trainingProgram.difficulty,
        trainingType: trainingProgram.trainingType,
        tags: trainingProgram.tags,
        numberOfWeeks: trainingProgram.numberOfWeeks,
        group: [trainingProgram.group],
        timesPerWeek: trainingProgram.timesPerWeek,
        equipmentType: trainingProgram.equipmentType,
        workoutDuration: workoutDurationValue,
      });
    }
  }, [trainingProgram]);

  useEffect(() => {
    if (error) {
      openNotification(
        error.message === "??" //TODO- get the right message
          ? t(
              "A program with these group, length, and duration fields already exists"
            )
          : undefined
      );
    }
  }, [error]);

  const openNotification = (error?: string, isWarning?: boolean) => {
    api[isWarning ? "warning" : "error"]({
      message: t(error || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const upsertTrainingProgram = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    let workoutDuration = null;
    let minWorkoutDuration = null;
    let maxWorkoutDuration = null;

    if (values.workoutDuration.includes("-")) {
      const [min, max] = values.workoutDuration.split("-").map(Number);
      minWorkoutDuration = min;
      maxWorkoutDuration = max;
    } else {
      workoutDuration = Number(values.workoutDuration);
    }

    const trainingProgram = {
      name: values.name,
      description: values.description,
      difficulty: values.difficulty,
      trainingType: values.trainingType,
      tags: values.tags || [],
      numberOfWeeks: values.numberOfWeeks,
      group: values.group[0],
      timesPerWeek: values.timesPerWeek,
      trainer: trainerId || "",
      equipmentType: values.equipmentType,
      workoutDuration,
      minWorkoutDuration,
      maxWorkoutDuration,
    };

    const upsertTrainingProgramBody: ICreateTrainingProgramPayload = {
      trainingProgram: { ...trainingProgram },
    };

    if (imageData) {
      const base64Image = await blobToBase64(imageData.binaryFile as Blob);
      upsertTrainingProgramBody.imageData = {
        ...imageData,
        binaryFile: base64Image,
        fileType: "image",
      };
    }

    if (largeImageData) {
      const base64Image = await blobToBase64(largeImageData.binaryFile as Blob);
      upsertTrainingProgramBody.largeImageData = {
        ...largeImageData,
        binaryFile: base64Image,
        fileType: "image",
      };
    }

    if (videoData) {
      const base64Video = await blobToBase64(videoData.binaryFile as Blob);
      upsertTrainingProgramBody.videoData = {
        ...videoData,
        binaryFile: base64Video,
        fileType: "video",
      };
    }
    if (!videoData?.binaryFile) {
      openNotification("Please note that no video was provided", true);
    }

    if (isNew) {
      dispatch(createTrainingProgramStart({ ...upsertTrainingProgramBody }));
    } else if (trainingProgramId) {
      dispatch(
        updateTrainingProgramStart({
          ...upsertTrainingProgramBody,
          trainingProgramId: trainingProgramId,
        })
      );
    }
  };

  const difficultyOptions = useDifficultyOptions();
  const trainingTypeOptions = useInterestsTags();
  const equipmentTypeOptions = useEquipmentTypeOptions();
  const tagsOptions = useFilterTags();
  const timesPerWeekOptions = Array.from({ length: 7 }, (_, i) => ({
    label: (i + 1).toString(),
    value: i + 1,
  }));

  return (
    <>
      {contextHolder}
      <RelativeFlex>
        <AbsoluteButton55 onClick={() => form.submit()}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton55>
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm
          form={form}
          onFinish={upsertTrainingProgram}
          validateTrigger="onSubmit"
        >
          <StyledLongLayout>
            <StyledColumnWrapper>
              <TextInputField
                name="name"
                label={t("Training Program Name")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />

              <SearchableSelectWithInput
                label={t("Group Assignment")}
                options={groupOptions}
                value={trainingProgram?.group}
                isEditable={isEditable}
                name="group"
                rules={[{ required: true, message: t("Required") }]}
              />
              <TextAreaField
                name="description"
                label={t("Description")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />
              <Flex gap={20}>
                <DropdownField
                  label={t("Difficulty")}
                  options={difficultyOptions}
                  value={trainingProgram?.difficulty}
                  isEditable={isEditable}
                  name="difficulty"
                  rules={[{ required: true, message: t("Required") }]}
                />
                <DropdownField
                  label={t("Number of Workouts per Week")}
                  options={timesPerWeekOptions}
                  value={trainingProgram?.timesPerWeek}
                  isEditable={isEditable}
                  name="timesPerWeek"
                  rules={[{ required: true, message: t("Required") }]}
                />
              </Flex>

              <Flex gap={20}>
                <TextInputField
                  name="numberOfWeeks"
                  label={t("Program Length in Weeks")}
                  isEditable={isEditable}
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid number"),
                    },
                  ]}
                />

                <TextInputField
                  name="workoutDuration"
                  label={t("Workout Duration in Minutes")}
                  isEditable={isEditable}
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^(\d+(-\d+)?)$/,
                      message: t("Invalid duraion"),
                    },
                  ]}
                />
              </Flex>
              <Flex gap={20}>
                <DropdownField
                  label={t("Program Type")}
                  options={trainingTypeOptions}
                  value={trainingProgram?.trainingType}
                  isEditable={isEditable}
                  name="trainingType"
                  rules={[{ required: true, message: t("Required") }]}
                />
                <DropdownField
                  label={t("Equipment Type")}
                  options={equipmentTypeOptions}
                  value={trainingProgram?.equipmentType}
                  isEditable={isEditable}
                  name="equipmentType"
                  rules={[{ required: true, message: t("Required") }]}
                />
              </Flex>
              <TagsDropdownField
                mode="multiple"
                label={t("Tags")}
                options={tagsOptions}
                name="tags"
                isEditable={isEditable}
              />
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Program Outer Cover Image")}
                isEditable={isEditable}
                value={
                  trainingProgram?.outerCover
                    ? IMAGE_BASE_URL + "/" + trainingProgram?.outerCover
                    : undefined
                }
                onChange={setImageData}
                aspect={349 / 134}
              />
              <ImageInputField
                label={t("Program Inner Cover Image")}
                isEditable={isEditable}
                value={
                  trainingProgram?.innerCover
                    ? IMAGE_BASE_URL + "/" + trainingProgram?.innerCover
                    : undefined
                }
                onChange={setLargeImageData}
                aspect={390 / 452}
              />
              <ImageInputField
                label={t("Program Cover Video (optional)")}
                isEditable={isEditable}
                value={
                  trainingProgram?.video
                    ? VIDEO_BASE_URL + "/" + trainingProgram?.video
                    : undefined
                }
                onChange={setVideoData}
                type="video"
                aspect={1}
              />
            </StyledColumnWrapper>
          </StyledLongLayout>
        </StyledForm>
      </Spin>
    </>
  );
};
